/**
 * ManualSelectPure
 *
 * Pure presentational component for selecting or creating reminder actions.
 * Receives all data and callbacks via props, no server dependencies.
 *
 * The Next.js wrapper (ManualSelect) provides tRPC data fetching and mutations.
 * Electron can use this directly with IPC-based data and callbacks.
 */
import { useCallback, useMemo, useState } from 'react';

import { cn } from '../cn';
import { FilterSelect } from '../Input2';
import { General, Time } from '../StrokeIcons';

// ============================================================================
// Types
// ============================================================================

export type ReminderAction = {
  id: string;
  name: string;
};

export type ManualSelectPureProps = {
  /** Current selected value (may be HTML from editor) */
  value?: string;
  /** Callback when value changes */
  onChange: (value: string) => void;
  /** Available reminder actions */
  reminderActions?: ReminderAction[];
  /** Whether reminder actions are loading */
  isLoading?: boolean;
  /** Callback to create a new reminder action */
  onCreateReminder?: (name: string) => Promise<void>;
  /** Whether a reminder is being created */
  isCreating?: boolean;
  /** Called when create state changes (for parent loading indicators) */
  onCreateStateChange?: (isPending: boolean) => void;
  /** Full width mode */
  fullWidth?: boolean;
  /** Additional className */
  className?: string;
  /** Function to convert editor HTML to plain text (optional) */
  convertToPlainText?: (html: string) => string;
  /** Callback for errors */
  onError?: (message: string) => void;
};

// ============================================================================
// Constants
// ============================================================================

const CREATE_PREFIX = '__create__:';

// ============================================================================
// Default converter (strips HTML tags)
// ============================================================================

function defaultConvertToPlainText(html: string): string {
  return html.replace(/<[^>]*>/g, '').trim();
}

// ============================================================================
// Component
// ============================================================================

export function ManualSelectPure({
  value,
  onChange,
  reminderActions = [],
  isLoading = false,
  onCreateReminder,
  isCreating = false,
  onCreateStateChange,
  fullWidth,
  className,
  convertToPlainText = defaultConvertToPlainText,
  onError,
}: ManualSelectPureProps) {
  const [filterQuery, setFilterQuery] = useState('');

  // Parse the current value (may be HTML from TipTap editor)
  const parsedValue = useMemo(
    () => (value ? convertToPlainText(value) : undefined),
    [value, convertToPlainText],
  );

  // Build options with optional "Create new" option
  const options = useMemo(() => {
    const baseOptions = reminderActions.map((m) => ({ value: m.name, label: m.name }));

    // Add "Create new" option AFTER existing matches so the user picks an
    // existing reminder by default and only falls through to create when
    // nothing else fits.
    const trimmedQuery = filterQuery.trim();
    if (trimmedQuery && onCreateReminder) {
      const exactMatch = baseOptions.some(
        (opt) => opt.label.toLowerCase() === trimmedQuery.toLowerCase(),
      );
      if (!exactMatch) {
        return [
          ...baseOptions,
          {
            value: `${CREATE_PREFIX}${trimmedQuery}`,
            label: `Create "${trimmedQuery}"`,
            icon: <General.Plus className="size-4" />,
          },
        ];
      }
    }

    return baseOptions;
  }, [reminderActions, filterQuery, onCreateReminder]);

  const handleChange = useCallback(
    async (selectedValue: string) => {
      // Check if this is a "create new" selection
      if (selectedValue.startsWith(CREATE_PREFIX)) {
        if (!onCreateReminder) return;

        const newName = selectedValue.slice(CREATE_PREFIX.length);
        try {
          onCreateStateChange?.(true);
          await onCreateReminder(newName);
          onChange(newName);
          setFilterQuery('');
        } catch (error) {
          onError?.((error as Error).message ?? 'Failed to create reminder');
        } finally {
          onCreateStateChange?.(false);
        }
      } else {
        onChange(selectedValue);
        setFilterQuery('');
      }
    },
    [onCreateReminder, onChange, onCreateStateChange, onError],
  );

  return (
    <FilterSelect
      icon={<Time.StepReminder />}
      options={options}
      value={parsedValue}
      onChange={handleChange}
      placeholder="select or create reminder"
      onFilterInputChange={setFilterQuery}
      status={isLoading || isCreating ? 'updating' : undefined}
      className={cn(fullWidth && 'w-full', className)}
      autoWidth
    />
  );
}
